import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RouteParams } from '../library/types';
import { RootState } from '../../reducers';
import { FileGridItemShell } from '../library/files/components/FileGridItemShell';
import { FileListItemShell } from '../library/files/components/FileListItemShell';

interface PendingFilesProps {
  view?: 'grid' | 'list';
}

const GridViewItem = ({
  progress,
  fileName,
  previewUrl,
  fileType,
}: {
  progress: number;
  fileName: string;
  previewUrl?: string;
  fileType?: string;
}) => {
  return (
    <FileGridItemShell
      name={fileName}
      thumbnailUrl={previewUrl}
      statusText="Uploading..."
      progress={progress}
      showPercentage
      fileType={fileType}
    />
  );
};

const ListViewItem = ({
  progress,
  fileName,
  previewUrl,
  fileType,
}: {
  progress: number;
  fileName: string;
  previewUrl?: string;
  fileType?: string;
}) => {
  return (
    <FileListItemShell
      name={fileName}
      thumbnailUrl={previewUrl}
      statusText="Uploading..."
      progress={progress}
      showPercentage
      fileType={fileType}
    />
  );
};

const PendingFiles = ({ view = 'list' }: PendingFilesProps) => {
  const { upload } = useSelector((state: RootState) => state);
  const { folderId } = useParams<RouteParams>();
  const { activeUploads } = upload;

  const filteredFiles = activeUploads.filter((file) => {
    if (
      folderId === 'recents' ||
      folderId === 'private' ||
      folderId === 'favorites'
    )
      return true;
    return file.folderId === folderId;
  });

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (filteredFiles.length > 0) {
        event.preventDefault();
      }
      return undefined;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [filteredFiles]);

  if (filteredFiles.length === 0) return null;

  if (view === 'grid') {
    return (
      <>
        {filteredFiles.map(
          ({ fileId, progress, fileName, previewUrl, fileType }) => (
            <GridViewItem
              key={fileId}
              progress={progress}
              fileName={fileName}
              previewUrl={previewUrl}
              fileType={fileType}
            />
          ),
        )}
      </>
    );
  }

  return (
    <>
      {filteredFiles.map(
        ({ fileId, progress, fileName, previewUrl, fileType }) => (
          <ListViewItem
            key={fileId}
            progress={progress}
            fileName={fileName}
            previewUrl={previewUrl}
            fileType={fileType}
          />
        ),
      )}
    </>
  );
};

export default PendingFiles;
